/**
 * ContractWizardDialog - Wizard de geração de contrato
 * Passos: 1) Selecionar contrato publicado, 2) Revisar variáveis, 3) Preview, 4) Signatários, 5) Confirmar
 * 
 * RELEASE: v2.0.1 - 2026-09-03 - Bloqueio do Workflow usa aviso modal global
 * RELEASE: v2.0.0 - 2026-02-19
 * - Passo 1 agora lista published_contracts (status=production) filtrados por workspace do deal
 * - Signatários pré-populados com default_signers do contrato publicado selecionado
 * - Métodos de entrega baseados no published_contract (não mais no template)
 */
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { normalizePhoneIdentifier } from '@/lib/phone-utils';
import { parseContractVariables, substituteVariables } from '@/lib/contractVariableParser';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, Check, Loader2, FileSignature, FileText, Settings, Eye, Users, Briefcase, FileCheck, Upload, X, Car } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { usePublishedContracts } from '@/hooks/usePublishedContracts';
import { useGenerateContractAtomic } from '@/hooks/useContracts';
import { useWorkflowGuardNotice } from '@/providers/WorkflowGuardNoticeProvider';
import { PREDEFINED_VARIABLES } from '@/types/contracts';
import type { PublishedContract, WizardDealItem } from '@/types/contracts';
import {
  formatRecurrenceLabel,
  formatTermLabel,
  calculateRecurringTotal,
  isRecurringService,
  type RecurrenceInterval,
  type TermType,
  type TermUnit,
} from '@/lib/crm/recurringServiceUtils';
import { WizardStepTemplate } from './WizardStepTemplate';
import { WizardStepVariables } from './WizardStepVariables';
import { WizardStepPreview } from './WizardStepPreview';
import { WizardStepSigners } from './WizardStepSigners';
import { WizardStepConfirm } from './WizardStepConfirm';
import { WizardStepDealSelect } from './WizardStepDealSelect';
import { WizardStepDocuments } from './WizardStepDocuments';
import { WizardStepUploadPdf } from './WizardStepUploadPdf';
// v2026-08: passo de seleção de veículos (Fase 7 do módulo de Veículos)
import { WizardStepVehicles } from './WizardStepVehicles';
import { useCustomerVehicles, VEHICLE_TYPE_LABELS, type CustomerVehicle } from '@/hooks/useCustomerVehicles';
import { useVehiclesModuleEnabled } from '@/hooks/useVehiclesModule';
import { usePublishedContractRequiredDocs } from '@/hooks/usePublishedContractRequiredDocs';
import { useCustomerDocuments } from '@/hooks/useCustomerDocuments';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CONTRACT_USER_BUCKET, buildWizardUploadPath } from '@/lib/contracts/pdfBucketResolver';

// Logger do módulo
const logger = createLogger('ContractWizard');

// Props do componente
interface ContractWizardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** ID do deal — quando omitido, wizard exibe Step 0 para seleção de negócio */
  dealId?: string;
  tenantId: string;
  contactId?: string | null;
}

/** Dados de um signatário no wizard */
export interface WizardSigner {
  signer_name: string;
  signer_email: string;
  signer_phone: string;
  signer_role: string;
  sign_order: number;
  /** Método de entrega do link de assinatura: email ou whatsapp */
  delivery_method: 'email' | 'whatsapp';
  /** Indica que este signatário foi pré-configurado no contrato publicado e não pode ser removido */
  isPreConfigured?: boolean;
  /** Indica que este signatário possui dados padrão pré-preenchidos (campos ficam desabilitados) */
  hasDefaultData?: boolean;
}

/**
 * Chaves possíveis de passos do wizard.
 * A ordem final é montada dinamicamente conforme o modo (standalone) e o
 * tipo do modelo selecionado (html vs uploaded_pdf).
 */
type WizardStepKey =
  | 'deal'
  | 'template'
  | 'variables'
  | 'preview'
  | 'upload_pdf'
  | 'vehicles'
  | 'signers'
  | 'documents'
  | 'confirm';

/** Catálogo de definições dos passos (label/ícone/descrição) por chave */
const STEP_DEFS: Record<WizardStepKey, { label: string; icon: any; description: string }> = {
  deal:       { label: 'Negócio',         icon: Briefcase,     description: 'Selecione o negócio vinculado ao contrato' },
  template:   { label: 'Modelo',          icon: FileText,      description: 'Selecione o modelo de contrato a ser gerado' },
  variables:  { label: 'Variáveis',       icon: Settings,      description: 'Preencha as variáveis do contrato' },
  preview:    { label: 'Preview',         icon: Eye,           description: 'Revise o contrato antes de gerar' },
  upload_pdf: { label: 'PDF do contrato', icon: Upload,        description: 'Envie o PDF específico deste contrato' },
  vehicles:   { label: 'Veículos',        icon: Car,           description: 'Selecione os veículos incluídos no contrato' },
  signers:    { label: 'Signatários',     icon: Users,         description: 'Defina quem precisa assinar' },
  documents:  { label: 'Documentos',      icon: FileCheck,     description: 'Anexe os documentos exigidos pelo contrato' },
  confirm:    { label: 'Confirmar',       icon: Check,         description: 'Confirme e gere o contrato' },
};

/**
 * Formata valor numérico como moeda BRL para exibição no contrato
 */
function formatCurrencyBR(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/**
 * Substitui variáveis de item em um template HTML
 * Função auxiliar reutilizada pelo loop formal e pelo fallback
 *
 * @release v1.2.0 - 2026-05-19 — Adicionadas variáveis de serviço:
 *   item_tipo, item_unidade, item_mensalidade, item_periodicidade,
 *   item_prazo (+ qtd/unidade), item_total_vigencia, item_desconto_percentual/fixo.
 *   Campos sem valor renderizam string vazia (evita "R$ 0,00" ruidoso).
 */
function replaceItemVars(template: string, item: WizardDealItem): string {
  // Helper: aplica formatter somente quando há valor; caso contrário retorna ''
  const fmt = <T,>(value: T | null | undefined, formatter: (v: T) => string): string =>
    value === null || value === undefined || value === '' ? '' : formatter(value);

  const tipoLabel =
    item.product_type === 'service' ? 'Serviço' : item.product_type === 'product' ? 'Produto' : '';
  const unidadeLabel = item.unit_type
    ? item.unit_type.charAt(0).toUpperCase() + item.unit_type.slice(1)
    : '';
  const periodicidade = formatRecurrenceLabel(item.recurrence_interval as RecurrenceInterval | null);
  const prazo = formatTermLabel(
    item.term_type as TermType | null,
    item.term_quantity ?? null,
    item.term_unit as TermUnit | null,
  );

  let row = template;
  row = row.replace(/\{\{item_nome\}\}/g, item.product_name || '');
  row = row.replace(/\{\{item_sku\}\}/g, item.sku || '');
  row = row.replace(/\{\{item_tipo\}\}/g, tipoLabel);
  row = row.replace(/\{\{item_unidade\}\}/g, unidadeLabel);
  row = row.replace(/\{\{item_categoria\}\}/g, item.category || '');
  row = row.replace(/\{\{item_subcategoria\}\}/g, item.subcategory || '');
  row = row.replace(/\{\{item_quantidade\}\}/g, String(item.quantity || 0));
  row = row.replace(/\{\{item_valor_unitario\}\}/g, formatCurrencyBR(item.unit_price || 0));
  row = row.replace(/\{\{item_mensalidade\}\}/g, fmt(item.recurring_price, formatCurrencyBR));
  row = row.replace(/\{\{item_periodicidade\}\}/g, periodicidade);
  row = row.replace(/\{\{item_prazo\}\}/g, prazo);
  row = row.replace(/\{\{item_prazo_quantidade\}\}/g, item.term_quantity ? String(item.term_quantity) : '');
  row = row.replace(/\{\{item_prazo_unidade\}\}/g, item.term_unit || '');
  row = row.replace(/\{\{item_desconto\}\}/g, formatCurrencyBR(item.discount || 0));
  row = row.replace(/\{\{item_desconto_percentual\}\}/g, item.discount_percent ? `${item.discount_percent}%` : '');
  row = row.replace(/\{\{item_desconto_fixo\}\}/g, fmt(item.discount_fixed, formatCurrencyBR));
  row = row.replace(/\{\{item_total\}\}/g, formatCurrencyBR(item.total || 0));
  row = row.replace(/\{\{item_total_vigencia\}\}/g, fmt(item.lifetime_total, formatCurrencyBR));
  return row;
}

/**
 * Substitui variáveis de veículo ({{veiculo_item_*}}) em uma linha de template.
 * @release v1.0.0 - 2026-08-09
 */
function replaceVehicleVars(template: string, vehicle: CustomerVehicle): string {
  const tipo =
    vehicle.vehicle_type === 'OTHER'
      ? vehicle.category_name || 'Outros'
      : VEHICLE_TYPE_LABELS[vehicle.vehicle_type];
  let row = template;
  row = row.replace(/\{\{veiculo_item_placa\}\}/g, vehicle.plate || '');
  row = row.replace(/\{\{veiculo_item_tipo\}\}/g, tipo || '');
  row = row.replace(/\{\{veiculo_item_marca\}\}/g, vehicle.brand_name || '');
  row = row.replace(/\{\{veiculo_item_modelo\}\}/g, vehicle.model_name || '');
  row = row.replace(/\{\{veiculo_item_ano\}\}/g, vehicle.year ? String(vehicle.year) : '');
  row = row.replace(/\{\{veiculo_item_cor\}\}/g, vehicle.color || '');
  row = row.replace(/\{\{veiculo_item_chassi\}\}/g, vehicle.chassis || '');
  row = row.replace(/\{\{veiculo_item_renavam\}\}/g, vehicle.renavam || '');
  row = row.replace(/\{\{veiculo_item_codigo_fipe\}\}/g, vehicle.fipe_code || '');
  row = row.replace(
    /\{\{veiculo_item_valor_fipe\}\}/g,
    vehicle.fipe_value ? formatCurrencyBR(vehicle.fipe_value) : '',
  );
  return row;
}

/**
 * Processa blocos de loop {{#veiculos}}...{{/veiculos}} no HTML.
 * Mesma estratégia do loop de produtos: loop formal + fallback por <tr>.
 * @release v1.0.0 - 2026-08-09
 */
function processVehicleLoop(html: string, vehicles: CustomerVehicle[]): string {
  const loopRegex = /\{\{#veiculos\}\}([\s\S]*?)\{\{\/veiculos\}\}/g;
  let hasProcessedLoop = false;

  html = html.replace(loopRegex, (_match, template: string) => {
    if (/\{\{veiculo_item_\w+\}\}/.test(template)) {
      hasProcessedLoop = true;
      if (vehicles.length === 0) return '';
      return vehicles.map((v) => replaceVehicleVars(template, v)).join('\n');
    }
    return '';
  });

  // Fallback: TipTap pode separar os marcadores da tabela
  if (!hasProcessedLoop && /\{\{veiculo_item_\w+\}\}/.test(html)) {
    const trRegex = /<tr[^>]*>(?:(?!<\/tr>)[\s\S])*?\{\{veiculo_item_\w+\}\}[\s\S]*?<\/tr>/gi;
    html = html.replace(trRegex, (trTemplate) => {
      if (vehicles.length === 0) return '';
      return vehicles.map((v) => replaceVehicleVars(trTemplate, v)).join('\n');
    });
  }

  return html;
}

/**
 * Processa blocos de loop {{#produtos}}...{{/produtos}} no HTML
 * Caso 1: Loop formal com conteúdo entre marcadores
 * Caso 2: Fallback inteligente - detecta {{item_*}} em <tr> fora do loop
 * 
 * O TipTap pode separar os marcadores da tabela, deixando o bloco vazio.
 * O fallback resolve isso detectando <tr> com variáveis item_* automaticamente.
 */
function processProductLoop(html: string, items: WizardDealItem[]): string {
  // Caso 1: Loop formal {{#produtos}}...conteúdo...{{/produtos}}
  const loopRegex = /\{\{#produtos\}\}([\s\S]*?)\{\{\/produtos\}\}/g;
  let hasProcessedLoop = false;

  html = html.replace(loopRegex, (_match, template: string) => {
    // Verifica se o conteúdo entre marcadores tem variáveis item_*
    if (/\{\{item_\w+\}\}/.test(template)) {
      hasProcessedLoop = true;
      if (items.length === 0) return '';
      return items.map((item) => replaceItemVars(template, item)).join('\n');
    }
    // Marcadores vazios - remover (fallback vai tratar)
    return '';
  });

  // Caso 2: Fallback - variáveis item_* em <tr> fora de loop formal
  if (!hasProcessedLoop && /\{\{item_\w+\}\}/.test(html)) {
    // Encontra <tr> que contém variáveis {{item_*}}
    const trRegex = /<tr[^>]*>(?:(?!<\/tr>)[\s\S])*?\{\{item_\w+\}\}[\s\S]*?<\/tr>/gi;
    html = html.replace(trRegex, (trTemplate) => {
      if (items.length === 0) return '';
      return items.map((item) => replaceItemVars(trTemplate, item)).join('\n');
    });
  }

  return html;
}

/**
 * Dialog wizard de geração de contrato
 */
export function ContractWizardDialog({
  open,
  onOpenChange,
  dealId,
  tenantId,
  contactId,
}: ContractWizardDialogProps) {
  // =====================================================================
  // Modo standalone: quando dealId não é fornecido, exibe Step 0 de seleção
  // =====================================================================
  const isStandalone = !dealId;
  const workflowNotice = useWorkflowGuardNotice();
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  /** Contato selecionado no Step 0 standalone (filtra negócios) */
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  /** dealId efetivo: externo (modo deal) ou selecionado no Step 0 (standalone) */
  const effectiveDealId = dealId || selectedDealId || '';

  // Contrato publicado selecionado (published_contract) — declarado antes de STEPS
  // porque a montagem dinâmica dos passos depende do tipo do modelo escolhido.
  const [selectedContract, setSelectedContract] = useState<PublishedContract | null>(null);

  // Template efetivo do contrato selecionado (versão fixada ou template atual)
  const selectedTemplateEarly =
    selectedContract?.contract_template_versions ?? selectedContract?.contract_templates ?? null;
  /**
   * v2026-05: identifica se o modelo escolhido é do tipo PDF pronto (upload).
   * Quando true, o wizard substitui as etapas "Variáveis" e "Preview" por uma
   * única etapa "PDF do contrato", em que o usuário envia o PDF específico deste
   * negócio (permitindo contratos personalizados por cliente).
   */
  const isUploadedPdfTemplate =
    (selectedTemplateEarly as any)?.source_type === 'uploaded_pdf';

  // =====================================================================
  // v2026-08: módulo de Veículos — seleção que alimenta {{veiculo_*}} e o
  // loop {{#veiculos}}, além do snapshot em contract_vehicles.
  // =====================================================================
  const vehiclesModuleEnabled = useVehiclesModuleEnabled();
  /** IDs dos veículos selecionados no passo "Veículos" */
  const [selectedVehicleIds, setSelectedVehicleIds] = useState<string[]>([]);
  /** Veículos já vinculados ao negócio */
  const { vehicles: dealVehicles, isLoading: isLoadingDealVehicles } = useCustomerVehicles(
    { dealId: effectiveDealId || null },
    open && vehiclesModuleEnabled && !!effectiveDealId,
  );
  /** Veículos do contato do negócio (não necessariamente vinculados ao deal) */
  const effectiveContactId = contactId || selectedContactId || null;
  const { vehicles: contactVehicles } = useCustomerVehicles(
    { contactId: effectiveContactId },
    open && vehiclesModuleEnabled && !!effectiveContactId,
  );

  /** União (sem duplicados) dos veículos disponíveis para o contrato */
  const availableVehicles = useMemo(() => {
    const map = new Map<string, CustomerVehicle>();
    [...dealVehicles, ...contactVehicles].forEach((v) => map.set(v.id, v));
    return Array.from(map.values());
  }, [dealVehicles, contactVehicles]);

  /** Exibe o passo "Veículos" apenas quando há veículos disponíveis */
  const showVehiclesStep = vehiclesModuleEnabled && availableVehicles.length > 0;

  /** Objetos completos dos veículos selecionados (ordem da listagem) */
  const selectedVehicles = useMemo(
    () => availableVehicles.filter((v) => selectedVehicleIds.includes(v.id)),
    [availableVehicles, selectedVehicleIds],
  );

  /**
   * Passos dinâmicos:
   *  - standalone acrescenta 'deal' no início;
   *  - modelo PDF pronto substitui variables/preview por 'upload_pdf'.
   */
  const STEPS = useMemo(() => {
    const keys: WizardStepKey[] = [];
    if (isStandalone) keys.push('deal');
    keys.push('template');
    if (isUploadedPdfTemplate) {
      keys.push('upload_pdf');
    } else {
      // v2026-08: veículos vêm antes das variáveis para alimentar {{veiculo_*}}
      if (showVehiclesStep) keys.push('vehicles');
      keys.push('variables', 'preview');
    }
    keys.push('signers', 'documents', 'confirm');
    return keys.map((k) => ({ key: k, ...STEP_DEFS[k] }));
  }, [isStandalone, isUploadedPdfTemplate, showVehiclesStep]);

  // Passo atual (0-based) e helper de chave atual
  const [step, setStep] = useState(0);
  const currentKey = STEPS[step]?.key;

  // Variáveis preenchidas
  const [variables, setVariables] = useState<Record<string, string>>({});
  // Itens de produto do deal (para loop e tabela editável)
  const [dealItems, setDealItems] = useState<WizardDealItem[]>([]);
  // Signatários definidos
  const [signers, setSigners] = useState<WizardSigner[]>([
    { signer_name: '', signer_email: '', signer_phone: '', signer_role: 'contratante', sign_order: 1, delivery_method: 'email' },
  ]);

  // v2026-05: PDF enviado no wizard para modelos do tipo 'uploaded_pdf'
  /** Caminho no bucket `contratos` do PDF enviado nesta sessão do wizard */
  const [wizardUploadedPdfPath, setWizardUploadedPdfPath] = useState<string | null>(null);
  /** Nome original do arquivo (apenas para exibição no resumo/confirmação) */
  const [wizardUploadedPdfName, setWizardUploadedPdfName] = useState<string | null>(null);
  /** Flag de upload em andamento (desabilita botões durante envio ao storage) */
  const [isUploadingWizardPdf, setIsUploadingWizardPdf] = useState(false);

  // Busca contratos em produção (published_contracts com status=production)
  const { productionContracts, isLoadingProduction } = usePublishedContracts();

  // Busca dados enriquecidos do deal + seller em um único roundtrip (join direto)
  // Eliminando a race condition causada por queries separadas para seller e deal
  const { data: dealData } = useQuery({
    queryKey: ['deal-for-contract', effectiveDealId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('crm_deals')
        .select(`
          id, title, value, currency, expected_close_date, source,
          contact:contacts (id, name, email, phone, metadata),
          pipeline:crm_pipelines (
            id,
            workspace:crm_workspaces (id, name)
          ),
          seller:crm_sellers (id, name, email, phone)
        `)
        .eq('id', effectiveDealId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: open && !!effectiveDealId,
  });

  // Busca customer vinculado ao deal (por customer_id ou via contact_id)
  const { data: customerData } = useQuery({
    queryKey: ['customer-for-contract', effectiveDealId],
    queryFn: async () => {
      // Tenta por customer_id primeiro
      const { data: deal } = await supabase
        .from('crm_deals')
        .select('customer_id, contact_id')
        .eq('id', effectiveDealId)
        .single();

      if (deal?.customer_id) {
        const { data } = await supabase
          .from('crm_customers')
          .select('*')
          .eq('id', deal.customer_id)
          .single();
        return data;
      }
      // Fallback: busca por main_contact_id
      if (deal?.contact_id) {
        const { data } = await supabase
          .from('crm_customers')
          .select('*')
          .eq('main_contact_id', deal.contact_id)
          .maybeSingle();
        return data;
      }
      return null;
    },
    enabled: open && !!effectiveDealId,
  });

  // Busca itens do deal (produtos/serviços) separadamente
  const { data: rawDealItems } = useQuery({
    queryKey: ['deal-items-for-contract', effectiveDealId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('crm_deal_items')
        .select(`
          id, quantity, unit_price, discount_percent, discount_fixed, total,
          product:crm_products (
            id, name, sku, product_type, unit_type,
            recurring_price, recurrence_interval, term_type, term_quantity, term_unit,
            category:crm_product_categories!category_id (
              id, name,
              parent:crm_product_categories!parent_id (id, name)
            )
          )
        `)
        .eq('deal_id', effectiveDealId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: open && !!effectiveDealId,
  });

  // Template efetivo: vem do published_contract (versão fixada ou template atual)
  const selectedTemplate = selectedContract?.contract_template_versions ?? selectedContract?.contract_templates ?? null;
  // Template base (sempre o contrato_templates para variaveis/content_html)
  const activeTemplate = selectedContract?.contract_templates ?? null;

  // Workspace ID do deal atual (para filtro no passo 1)
  const dealWorkspaceId = (dealData?.pipeline as any)?.workspace?.id ?? null;

  // Estado da URL assinada da imagem de fundo do template selecionado
  const [backgroundUrl, setBackgroundUrl] = useState<string | null>(null);

  // Resolve URL da imagem de fundo quando template é selecionado
  useEffect(() => {
    const bgPath = (selectedTemplate as any)?.background_image_path;
    const fetchBgUrl = async () => {
      if (!bgPath) {
        setBackgroundUrl(null);
        return;
      }
      if (bgPath.startsWith('http')) {
        setBackgroundUrl(bgPath);
        return;
      }
      try {
        const { data } = await supabase.storage
          .from('contratos')
          .createSignedUrl(bgPath, 3600);
        setBackgroundUrl(data?.signedUrl || null);
      } catch {
        setBackgroundUrl(null);
      }
    };
    fetchBgUrl();
  }, [(selectedTemplate as any)?.background_image_path]);

  /**
   * Re-sincroniza signatários quando dealData ou customerData chegam APÓS a seleção do contrato
   * Resolve race condition: handleSelectContract pode executar antes das queries completarem
   * Só preenche campos que ainda estão vazios — respeita edições manuais do usuário
   */
  useEffect(() => {
    if (!selectedContract || !dealData) return;

    const seller = (dealData as any)?.seller as { name?: string; email?: string | null; phone?: string | null } | null;
    const contact = dealData?.contact as { name?: string; email?: string | null; phone?: string | null } | null;

    // Função local de normalização de telefone para o useEffect
    const normalizePhoneLocal = (phone: string | null | undefined): string => {
      if (!phone) return '';
      const normalized = normalizePhoneIdentifier(phone);
      return normalized ? (normalized.startsWith('+') ? normalized : `+${normalized}`) : '';
    };

    setSigners(prev => prev.map(signer => {
      // Preenche vendedor apenas se ainda estiver vazio e o seller existir no deal
      if (signer.signer_role === 'vendedor' && !signer.signer_name && seller?.name) {
        return {
          ...signer,
          signer_name: seller.name || '',
          signer_email: seller.email || '',
          signer_phone: normalizePhoneLocal(seller.phone),
        };
      }
      // Preenche contratante apenas se ainda estiver vazio e os dados existirem
      if (signer.signer_role === 'contratante' && !signer.signer_name) {
        const name =
          (customerData as any)?.razao_social ||
          (customerData as any)?.nome_fantasia ||
          (customerData as any)?.nome ||
          contact?.name ||
          '';
        if (name) {
          return {
            ...signer,
            signer_name: name,
            signer_email: contact?.email || '',
            signer_phone: normalizePhoneLocal(contact?.phone),
          };
        }
      }
      return signer;
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealData, customerData, selectedContract]);

  // Mutation atômica: contrato + signatários + documentos em uma única RPC
  const generateContract = useGenerateContractAtomic();

  // Documentos exigidos pelo contrato publicado selecionado
  const {
    requiredDocs,
  } = usePublishedContractRequiredDocs(selectedContract?.id);

  // Documentos existentes do cliente (para reutilização no wizard)
  const customerIdForDocs = customerData?.id || null;
  const {
    documents: customerDocs,
    uploadDocument: uploadCustomerDoc,
    getPublicUrl,
  } = useCustomerDocuments(customerIdForDocs, open && !!selectedContract);

  /** Handler de upload de documento do wizard */
  const handleDocUpload = async (file: File, documentTypeId: string) => {
    await uploadCustomerDoc.mutateAsync({ file, document_type_id: documentTypeId });
  };

  /**
   * Pré-preenche variáveis quando published_contract é selecionado
   * Usa dados do deal/contato e pré-popula signatários com default_signers do contrato publicado
   */
  const handleSelectContract = (publishedContract: PublishedContract) => {
    setSelectedContract(publishedContract);

    // Conteúdo HTML: versão fixada tem prioridade, fallback para template atual
    const templateForVars = publishedContract.contract_template_versions ?? publishedContract.contract_templates;
    const contentHtml = (templateForVars as any)?.content_html || '';

    // Método de entrega padrão baseado no published_contract
    const defaultMethod = (publishedContract.allowed_delivery_methods?.[0] as 'email' | 'whatsapp') || 'email';

    // Pré-preenche com dados do deal/contato
    const contact = dealData?.contact as { name?: string; email?: string; phone?: string; metadata?: Record<string, unknown> } | null;
    const pipeline = dealData?.pipeline as { workspace?: { name?: string } } | null;
    const prefilled: Record<string, string> = {};

    // Mapeia variáveis pré-definidas com dados do deal
    PREDEFINED_VARIABLES.forEach((v) => {
      if (v.source === 'contact' && contact) {
        if (v.source_field === 'name') prefilled[v.key] = contact.name || '';
        if (v.source_field === 'email') prefilled[v.key] = contact.email || '';
        if (v.source_field === 'phone') prefilled[v.key] = contact.phone || '';
        if (v.source_field === 'document') {
          prefilled[v.key] = customerData?.cpf || customerData?.cnpj || '';
        }
        if (v.source_field === 'birth_date') {
          const dt = customerData?.data_nascimento;
          prefilled[v.key] = dt ? new Date(dt + 'T00:00:00').toLocaleDateString('pt-BR') : '';
        }
        if (v.source_field === 'address' || v.source_field === 'address_full') {
          const parts = [customerData?.logradouro, customerData?.numero, customerData?.complemento, customerData?.bairro].filter(Boolean);
          prefilled[v.key] = parts.join(', ');
        }
        if (v.source_field === 'city_state') {
          prefilled[v.key] = [customerData?.cidade, customerData?.estado].filter(Boolean).join(', ');
        }
        if (v.source_field === 'zip_code') {
          prefilled[v.key] = customerData?.cep || '';
        }
      }
      if (v.source === 'deal' && dealData) {
        if (v.source_field === 'title') prefilled[v.key] = dealData.title || '';
        if (v.source_field === 'value') prefilled[v.key] = dealData.value?.toString() || '';
        if (v.source_field === 'currency') prefilled[v.key] = dealData.currency || 'BRL';
        if (v.source_field === 'expected_close_date') prefilled[v.key] = dealData.expected_close_date || '';
        if (v.source_field === 'workspace_name') prefilled[v.key] = pipeline?.workspace?.name || '';
      }
      if (v.source === 'custom' && v.key === 'data_hoje') {
        prefilled[v.key] = new Date().toLocaleDateString('pt-BR');
      }
    });

    // Converte itens do deal para formato do wizard.
    // @release v1.2.0 — captura campos de serviço (avulso/recorrente) para uso no template.
    const wizardItems: WizardDealItem[] = (rawDealItems || []).map((item: any) => {
      const product = item.product as any;
      const category = product?.category as any;
      const qty = item.quantity || 1;
      const price = item.unit_price || 0;
      const discountValue = (item.discount_percent || 0) * qty * price / 100 + (item.discount_fixed || 0);
      const recurring = isRecurringService(product?.product_type, product?.unit_type);
      const lifetime = recurring
        ? calculateRecurringTotal({
            recurring_price: product?.recurring_price,
            recurrence_interval: product?.recurrence_interval,
            term_type: product?.term_type,
            term_quantity: product?.term_quantity,
            term_unit: product?.term_unit,
          })
        : null;
      return {
        id: item.id,
        product_name: product?.name || '',
        sku: product?.sku || '',
        category: category?.parent?.name || category?.name || '',
        subcategory: category?.parent ? (category?.name || '') : '',
        quantity: qty,
        unit_price: price,
        discount: discountValue,
        total: item.total || (qty * price - discountValue),
        product_type: product?.product_type ?? null,
        unit_type: product?.unit_type ?? null,
        recurring_price: product?.recurring_price ?? null,
        recurrence_interval: product?.recurrence_interval ?? null,
        term_type: product?.term_type ?? null,
        term_quantity: product?.term_quantity ?? null,
        term_unit: product?.term_unit ?? null,
        discount_percent: item.discount_percent ?? null,
        discount_fixed: item.discount_fixed ?? null,
        lifetime_total: lifetime ? lifetime * qty : null,
      };
    });
    setDealItems(wizardItems);

    if (wizardItems.length === 1) {
      const p = wizardItems[0];
      prefilled['categoria_produto'] = p.category;
      prefilled['subcategoria_produto'] = p.subcategory;
      prefilled['sku_produto'] = p.sku;
      prefilled['quantidade_produto'] = String(p.quantity);
      prefilled['valor_produto'] = String(p.unit_price);
      prefilled['desconto'] = String(p.discount);
      prefilled['valor_total'] = String(p.total);
    }

    // =====================================================================
    // Totais agregados (espelham os cards da aba Produtos do modal de negócio)
    // @release v1.2.0 - 2026-05-19
    // =====================================================================
    const subtotal = wizardItems.reduce((sum, i) => sum + (i.unit_price * i.quantity), 0);
    const descontos = wizardItems.reduce((sum, i) => sum + (i.discount || 0), 0);
    const totalNeg = wizardItems.reduce((sum, i) => sum + (i.total || 0), 0);
    const mrr = wizardItems.reduce((sum, i) => {
      if (!isRecurringService(i.product_type, i.unit_type)) return sum;
      // Aproxima MRR: para recorrência mensal usa direto; outras escalas são tratadas como mensal-equivalente
      const mensal = (i.recurring_price || 0) * (i.quantity || 1);
      return sum + mensal;
    }, 0);
    const lifetimeTotal = wizardItems.reduce((sum, i) => sum + (i.lifetime_total || 0), 0);
    const produtosCount = wizardItems.filter((i) => i.product_type === 'product').length;
    const servicosCount = wizardItems.filter((i) => i.product_type === 'service').length;
    const hasRecurring = wizardItems.some((i) => isRecurringService(i.product_type, i.unit_type));

    prefilled['subtotal_negocio'] = formatCurrencyBR(subtotal);
    prefilled['desconto_total'] = formatCurrencyBR(descontos);
    prefilled['total_negocio'] = formatCurrencyBR(totalNeg);
    prefilled['mrr_total'] = formatCurrencyBR(mrr);
    prefilled['total_vigencia_contrato'] = lifetimeTotal > 0 ? formatCurrencyBR(lifetimeTotal) : '';
    prefilled['quantidade_itens'] = String(wizardItems.length);
    prefilled['quantidade_produtos'] = String(produtosCount);
    prefilled['quantidade_servicos'] = String(servicosCount);
    prefilled['tem_servico_recorrente'] = hasRecurring ? 'Sim' : 'Não';


    // =====================================================================
    // Pré-popula signatários baseado nos default_signers do published_contract
    // Se não há default_signers configurados, usa o contato do deal como contratante
    // v2: inclui auto-preenchimento para papel "vendedor" (dados do crm_sellers)
    //     e melhora "contratante" usando customerData quando disponível
    // =====================================================================

    /**
     * Normaliza telefone para formato E.164 (+DDDNUMERO)
     * Usado tanto para contato quanto para vendedor
     */
    const normalizePhone = (phone: string | null | undefined): string => {
      if (!phone) return '';
      const normalized = normalizePhoneIdentifier(phone);
      return normalized ? (normalized.startsWith('+') ? normalized : `+${normalized}`) : '';
    };

    // Dados do vendedor vindos do join no dealData (single roundtrip)
    const seller = (dealData as any)?.seller as { name?: string; email?: string | null; phone?: string | null } | null;

    // Telefones normalizados
    const contactPhone = normalizePhone(contact?.phone);
    const sellerPhone = normalizePhone(seller?.phone);

    // Nome do contratante: prioriza customerData (PJ: razao_social/nome_fantasia; PF: nome), depois contato
    const contratanteName =
      customerData?.razao_social ||
      customerData?.nome_fantasia ||
      customerData?.nome ||
      contact?.name ||
      '';

    // E-mail do contratante: contato tem preferência (campo direto)
    const contratanteEmail = contact?.email || '';

    // Telefone do contratante: contato tem preferência
    const contratantePhone = contactPhone;

    if (publishedContract.default_signers && publishedContract.default_signers.length > 0) {
      // Monta lista de signatários com auto-preenchimento por papel
      const prePopulatedSigners: WizardSigner[] = publishedContract.default_signers.map((ds) => {
        // Contratante: dados do cliente/contato do deal
        if (ds.signer_role === 'contratante') {
          return {
            signer_name: contratanteName,
            signer_email: contratanteEmail,
            signer_phone: contratantePhone,
            signer_role: ds.signer_role,
            sign_order: ds.sign_order,
            delivery_method: (ds.delivery_method as 'email' | 'whatsapp') || defaultMethod,
            isPreConfigured: true, // pré-configurado: não pode ser removido
          };
        }
        // Vendedor: dados do crm_sellers via join na query deal-for-contract
        if (ds.signer_role === 'vendedor') {
          return {
            signer_name: seller?.name || '',
            signer_email: seller?.email || '',
            signer_phone: sellerPhone,
            signer_role: ds.signer_role,
            sign_order: ds.sign_order,
            delivery_method: (ds.delivery_method as 'email' | 'whatsapp') || defaultMethod,
            isPreConfigured: true, // pré-configurado: não pode ser removido
          };
        }
        // Demais papéis (contratado, testemunha, fiador, representante_legal): usa dados padrão se configurados
        return {
          signer_name: ds.default_name || '',
          signer_email: ds.default_email || '',
          signer_phone: ds.default_phone || '',
          signer_role: ds.signer_role,
          sign_order: ds.sign_order,
          delivery_method: (ds.delivery_method as 'email' | 'whatsapp') || defaultMethod,
          isPreConfigured: true, // pré-configurado: não pode ser removido
          hasDefaultData: !!(ds.default_name || ds.default_email || ds.default_phone),
        };
      });
      setSigners(prePopulatedSigners);
    } else if (contact?.name || contratanteName) {
      // Sem default_signers: usa dados do cliente/contato como contratante padrão
      setSigners([{
        signer_name: contratanteName,
        signer_email: contratanteEmail,
        signer_phone: contratantePhone,
        signer_role: 'contratante',
        sign_order: 1,
        delivery_method: defaultMethod,
      }]);
    }

    // Aplica defaultValue das meta-variáveis para campos sem mapeamento automático
    if (contentHtml) {
      const parsedVars = parseContractVariables(contentHtml);
      parsedVars.forEach((v) => {
        if (v.defaultValue && !prefilled[v.key]) {
          prefilled[v.key] = v.defaultValue;
        }
      });
    }

    setVariables(prefilled);
    // Avança para o próximo passo (variables OU upload_pdf, conforme o tipo do modelo)
    setStep((s) => s + 1);
  };

  /**
   * Gera o HTML final substituindo variáveis (com suporte a meta-variáveis) e loops de produto
   */
  /**
   * Variáveis efetivas = variáveis do formulário + variáveis derivadas dos
   * veículos selecionados ({{veiculo_*}} referem-se ao primeiro selecionado).
   * @release v2026-08
   */
  const effectiveVariables = useMemo(() => {
    const merged: Record<string, string> = { ...variables };
    const first = selectedVehicles[0];
    if (first) {
      merged['veiculo_placa'] = first.plate || '';
      merged['veiculo_tipo'] =
        first.vehicle_type === 'OTHER'
          ? first.category_name || 'Outros'
          : VEHICLE_TYPE_LABELS[first.vehicle_type];
      merged['veiculo_marca'] = first.brand_name || '';
      merged['veiculo_modelo'] = first.model_name || '';
      merged['veiculo_ano'] = first.year ? String(first.year) : '';
      merged['veiculo_cor'] = first.color || '';
      merged['veiculo_chassi'] = first.chassis || '';
      merged['veiculo_renavam'] = first.renavam || '';
      merged['veiculo_codigo_fipe'] = first.fipe_code || '';
      merged['veiculo_valor_fipe'] = first.fipe_value ? formatCurrencyBR(first.fipe_value) : '';
    }
    merged['quantidade_veiculos'] = String(selectedVehicles.length);
    return merged;
  }, [variables, selectedVehicles]);

  const finalHtml = useMemo(() => {
    const contentHtml = (selectedTemplate as any)?.content_html || '';
    if (!contentHtml) return '';
    let html = contentHtml;
    html = processProductLoop(html, dealItems);
    // v2026-08: loop de veículos processado antes da substituição simples
    html = processVehicleLoop(html, selectedVehicles);
    html = substituteVariables(html, effectiveVariables);
    return html;
  }, [selectedTemplate, effectiveVariables, dealItems, selectedVehicles]);

  /** Lock local para impedir duplo-click no botão "Gerar Contrato" */
  const [isGenerating, setIsGenerating] = useState(false);

  /**
   * Confirma e cria o contrato + signatários
   * Protegido contra duplo-click via isGenerating + tratamento de unique violation
   */
  const handleConfirm = async () => {
    if (!selectedContract || isGenerating) return;
    setIsGenerating(true);
    const templateId = selectedContract.template_id;
    const templateName = selectedContract.contract_templates?.name || selectedContract.name;

    try {
      // =====================================================================
      // RASTREABILIDADE DE VERSÃO: resolve qual versão foi efetivamente usada
      // - Versão fixada no published_contract tem prioridade absoluta
      // - Se "Mais recente" (template_version_id=NULL), busca a versão atual
      //   e grava o ID exato — assim nunca perdemos qual snapshot foi usado
      // =====================================================================
      const fixedVersionId = selectedContract.template_version_id || null;
      let usedVersionId: string | null = fixedVersionId;

      if (!fixedVersionId) {
        // RELEASE v2.0.0 - 2026-05-10: server-side via get_contract_template_versions
        // (retorna ordenado DESC; primeira linha = versão mais recente)
        const { data: versions } = await supabase.rpc('get_contract_template_versions', {
          p_template_id: templateId,
        });
        const latestVersion = Array.isArray(versions) && versions.length > 0 ? versions[0] : null;
        usedVersionId = latestVersion?.id || null;
        logger.info('Versão mais recente resolvida para rastreabilidade', {
          templateId,
          usedVersionId,
          versionNumber: latestVersion?.version_number,
        });
      }

      // Monta snapshots dos documentos anexados ao contrato
      const documentSnapshots = requiredDocs
        .map((req) => {
          const custDoc = customerDocs.find((d) => d.document_type_id === req.document_type_id);
          if (!custDoc) return null;
          return {
            customer_document_id: custDoc.id,
            document_type_id: req.document_type_id,
            was_required: req.is_required,
            snapshot_file_name: custDoc.file_name,
            snapshot_storage_path: custDoc.storage_path,
          };
        })
        .filter(Boolean) as Array<{
          customer_document_id: string;
          document_type_id: string;
          was_required: boolean;
          snapshot_file_name: string;
          snapshot_storage_path: string;
        }>;

      // v2026-05: se o template é PDF pronto, o backend cria o contrato já 'generated'
      // apontando para o mesmo arquivo, sem passar pela fila de renderização HTML.
      const templateSourceType = (selectedTemplate as any)?.source_type as
        | 'html'
        | 'uploaded_pdf'
        | undefined;
      const templatePdfPath = (selectedTemplate as any)?.source_pdf_storage_path as
        | string
        | null
        | undefined;

      // Chamada atômica: contrato + signatários + documentos em transação única
      await generateContract.mutateAsync({
        deal_id: effectiveDealId,
        template_id: templateId,
        title: `${templateName} - ${dealData?.title || 'Contrato'}`,
        content_html: templateSourceType === 'uploaded_pdf' ? '' : finalHtml,
        variables_data: templateSourceType === 'uploaded_pdf' ? {} : effectiveVariables,
        used_template_version_id: usedVersionId,
        published_contract_id: selectedContract.id,
        signers: signers.filter((s) => s.signer_name.trim()),
        document_snapshots: documentSnapshots,
        source_pdf_storage_path:
          templateSourceType === 'uploaded_pdf'
            ? (wizardUploadedPdfPath ?? templatePdfPath ?? null)
            : null,
        // v2026-08: snapshot atômico dos veículos selecionados
        vehicle_ids: selectedVehicleIds,
      });


      handleClose();
    } catch (error: any) {
      logger.error('Erro ao gerar contrato', error);
      // Bloqueio do processo aparece como aviso modal, não como falha técnica.
      if (workflowNotice.showWorkflowGuardNotice(error)) return;
      // Detecta unique violation (código 23505) para exibir mensagem amigável
      const errorMsg = error?.message || '';
      if (errorMsg.includes('idx_contracts_unique_draft_per_published') || errorMsg.includes('23505') || errorMsg.includes('duplicate key')) {
        toast.error('Já existe um rascunho deste modelo para este negócio. Exclua o rascunho existente antes de gerar um novo.');
      } else {
        toast.error('Erro ao gerar contrato');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  /** Fecha e reseta wizard */
  const handleClose = () => {
    setStep(0);
    setSelectedContract(null);
    setVariables({});
    setDealItems([]);
    setSelectedVehicleIds([]);
    setSelectedDealId(null);
    setSigners([{ signer_name: '', signer_email: '', signer_phone: '', signer_role: 'contratante', sign_order: 1, delivery_method: 'email' }]);
    // v2026-05: limpa estado do upload de PDF do wizard
    setWizardUploadedPdfPath(null);
    setWizardUploadedPdfName(null);
    setIsUploadingWizardPdf(false);
    onOpenChange(false);
  };

  /**
   * v2026-07: upload do PDF específico do contrato dentro do wizard.
   *
   * Reaproveita o bucket `anexos` (mesmo bucket já validado usado por
   * anexos de deal, notas, documentos e atividades) para evitar duplicar
   * políticas RLS no bucket legado `contratos`. O caminho segue o padrão:
   *   `{tenantId}/contract-wizard-uploads/{uuid}.pdf`
   *
   * O bucket correto é resolvido no download via `resolveContractPdfBucket`.
   */
  const handleWizardPdfUpload = async (file: File) => {
    if (!file) return;
    // Validação básica de tipo/extensão
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Selecione um arquivo PDF válido.');
      return;
    }
    try {
      setIsUploadingWizardPdf(true);
      // Nome único para evitar colisões
      const uniqueId = (globalThis.crypto as any)?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const path = buildWizardUploadPath(tenantId, uniqueId);
      const { error: upErr } = await supabase.storage
        .from(CONTRACT_USER_BUCKET)
        .upload(path, file, {
          contentType: 'application/pdf',
          upsert: false,
        });
      if (upErr) throw upErr;
      setWizardUploadedPdfPath(path);
      setWizardUploadedPdfName(file.name);
      toast.success('PDF enviado com sucesso.');
    } catch (err: any) {
      // Log com detalhes seguros para diagnóstico (sem expor dados sensíveis)
      logger.error('Falha ao enviar PDF do wizard', {
        message: err?.message,
        statusCode: err?.statusCode ?? err?.status,
        name: err?.name,
      });
      toast.error('Não foi possível enviar o PDF. Tente novamente.');
    } finally {
      setIsUploadingWizardPdf(false);
    }
  };

  /** Remove o PDF enviado no wizard (limpa somente o estado; o arquivo fica órfão) */
  const handleClearWizardPdf = () => {
    setWizardUploadedPdfPath(null);
    setWizardUploadedPdfName(null);
  };


  /** Verifica se pode avançar — validação por chave do passo atual */
  const canNext = () => {
    switch (currentKey) {
      case 'deal':
        return !!selectedDealId;
      case 'template':
        return !!selectedContract;
      case 'variables': {
        const contentHtml = (selectedTemplate as any)?.content_html || '';
        if (!contentHtml) return true;
        const parsed = parseContractVariables(contentHtml);
        return parsed
          .filter((v) => v.required)
          .every((v) => (variables[v.key] ?? v.defaultValue ?? '').trim());
      }
      case 'upload_pdf':
        // Só libera avançar quando o PDF já foi enviado com sucesso ao storage
        return !!wizardUploadedPdfPath && !isUploadingWizardPdf;
      case 'signers': {
        const allHaveName = signers.every((s) => s.signer_name.trim() !== '');
        if (!allHaveName) return false;

        const emailOk = signers
          .filter((s) => s.delivery_method === 'email')
          .every((s) => s.signer_email.trim() !== '');
        if (!emailOk) return false;

        const whatsappOk = signers
          .filter((s) => s.delivery_method === 'whatsapp')
          .every((s) => {
            const allDigits = s.signer_phone.replace(/\D/g, '');
            const localDigits = allDigits.startsWith('55') && allDigits.length > 10
              ? allDigits.slice(2)
              : allDigits;
            return localDigits.length >= 10;
          });
        return whatsappOk;
      }
      case 'documents': {
        const requiredMissing = requiredDocs
          .filter((r) => r.is_required)
          .some((r) => !customerDocs.find((d) => d.document_type_id === r.document_type_id));
        return !requiredMissing;
      }
      default:
        return true;
    }
  };

  const isSubmitting = generateContract.isPending || isGenerating;

  // Métodos de entrega permitidos: vem do published_contract selecionado
  const allowedDeliveryMethods = (selectedContract?.allowed_delivery_methods || ['email']) as ('email' | 'whatsapp')[];

  // Template para preview: versão fixada ou template atual (cast para ContractTemplate)
  const activeTemplateForPreview = (selectedTemplate as any) ?? null;

  /** Navegação simples: a montagem dinâmica de STEPS já cuida das etapas visíveis */
  const goNextStep = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const goPrevStep = () => setStep((s) => Math.max(s - 1, 0));



  // Contagem de signatários com nome preenchido (para resumo na sidebar)
  const filledSignersCount = signers.filter((s) => s.signer_name.trim()).length;

  // Progresso percentual para a barra da sidebar
  const progressPercent = Math.round(((step + 1) / STEPS.length) * 100);

  // Ícone e descrição do passo atual
  const CurrentStepIcon = STEPS[step].icon;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      {/*
       * Layout de 2 colunas: sidebar esquerda (280px) + conteúdo direito
       * Identidade visual: gradiente verde/teal escuro (distinto do azul de campanhas)
       * Segue exatamente o padrão visual do CreateCampaignDialog
       */}
      <DialogContent
        className="max-w-5xl max-h-[90vh] h-[90vh] p-0 overflow-hidden gap-0 flex flex-col"
        hideCloseButton
      >
        {/* Título acessível obrigatório para Dialog (sr-only) */}
        <DialogHeader className="sr-only">
          <DialogTitle>Gerar Contrato</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] flex-1 min-h-0 overflow-hidden">

          {/* ============================================================
              SIDEBAR ESQUERDA — Identidade visual verde/teal de Contratos
              ============================================================ */}
          <aside className="hidden md:flex flex-col h-full overflow-hidden [background:linear-gradient(160deg,hsl(160,70%,10%)_0%,hsl(160,60%,16%)_55%,hsl(150,50%,20%)_100%)]">
            {/* Header da sidebar: ícone + título + progresso */}
            <div className="px-5 pt-6 pb-4 border-b border-white/10">
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(255,255,255,0.12)' }}
                >
                  <FileSignature className="h-5 w-5 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-white font-semibold text-sm truncate">Gerar Contrato</p>
                  <p className="text-white/60 text-xs">{step + 1} de {STEPS.length} etapas</p>
                </div>
              </div>
              {/* Barra de progresso */}
              <Progress
                value={progressPercent}
                className="h-1.5"
                style={{ background: 'rgba(255,255,255,0.15)' }}
                indicatorClassName="bg-white"
              />
            </div>

            {/* Lista de passos clicáveis */}
            <ScrollArea className="flex-1 px-3 py-3" axis="y">
              <div className="space-y-0.5">
                {STEPS.map((s, i) => {
                  const StepIcon = s.icon;
                  const isActive = i === step;
                  const isDone   = i < step;
                  return (
                    <button
                      key={s.label}
                      type="button"
                      disabled={!isDone}
                      onClick={() => isDone && setStep(i)}
                      className={cn(
                        'w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all',
                        isActive
                          ? 'bg-white/15 text-white'
                          : isDone
                            ? 'text-white/70 hover:bg-white/10 hover:text-white cursor-pointer'
                            : 'text-white/35 cursor-default'
                      )}
                    >
                      {/* Indicador circular numerado */}
                      <div className={cn(
                        'h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-all',
                        isActive
                          ? 'bg-background text-primary'
                          : isDone
                            ? 'bg-white/25 text-white'
                            : 'bg-white/10 text-white/40'
                      )}>
                        {isDone ? <Check className="h-3.5 w-3.5" /> : <StepIcon className="h-3 w-3" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-semibold truncate">{s.label}</p>
                        <p className={cn(
                          'text-[10px] leading-tight truncate',
                          isActive ? 'text-white/75' : 'text-white/40'
                        )}>{s.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </ScrollArea>

            {/* Resumo em tempo real na base da sidebar */}
            <div className="px-4 py-4 border-t border-white/10 space-y-2">
              <p className="text-white/40 text-[10px] uppercase tracking-wider font-semibold mb-1">Resumo</p>
              <div className="space-y-1.5">
                <div className="flex items-start gap-2">
                  <FileText className="h-3 w-3 text-white/40 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-white/40 text-[10px]">Modelo</p>
                    <p className="text-white/80 text-xs font-medium truncate">
                      {selectedContract?.name || '—'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Users className="h-3 w-3 text-white/40 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-white/40 text-[10px]">Signatários</p>
                    <p className="text-white/80 text-xs font-medium">
                      {filledSignersCount > 0 ? `${filledSignersCount} configurado${filledSignersCount > 1 ? 's' : ''}` : '—'}
                    </p>
                  </div>
                </div>
                {dealData?.title && (
                  <div className="flex items-start gap-2">
                    <FileSignature className="h-3 w-3 text-white/40 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-white/40 text-[10px]">Negócio</p>
                      <p className="text-white/80 text-xs font-medium truncate">{dealData.title}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </aside>

          {/* ============================================================
              ÁREA PRINCIPAL DIREITA — Header fixo + Conteúdo scrollável + Footer
              ============================================================ */}
          <main className="flex flex-col h-full overflow-hidden bg-background">

            {/* Header do passo atual */}
            <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <CurrentStepIcon className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-foreground">{STEPS[step].label}</h2>
                  <p className="text-xs text-muted-foreground">{STEPS[step].description}</p>
                </div>
              </div>
              {/* Badge de progresso mobile (visível apenas em telas pequenas) */}
              <div className="md:hidden text-xs text-muted-foreground font-medium">
                {step + 1}/{STEPS.length}
              </div>
              {/* Botão fechar */}
              <button
                type="button"
                onClick={handleClose}
                className="hidden md:flex h-7 w-7 rounded-md items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                aria-label="Fechar"
              >
                <ArrowLeft className="h-4 w-4 rotate-[135deg]" />
              </button>
            </div>

            {/* Área scrollável com conteúdo do passo */}
            <ScrollArea className="flex-1 min-h-0" axis="y">
              <div className="px-6 py-5">
                {/* Step 0 standalone: Seleção de negócio via DealSelector */}
                {currentKey === 'deal' && (
                  <WizardStepDealSelect
                    value={selectedDealId}
                    onSelect={setSelectedDealId}
                    contactId={selectedContactId}
                    onContactSelect={setSelectedContactId}
                    onDealData={(deal) => {
                      // Fluxo inverso: ao selecionar deal, preenche contato automaticamente
                      if (deal?.contact_id && !selectedContactId) {
                        setSelectedContactId(deal.contact_id);
                      }
                    }}
                  />
                )}
                {/* Passo "Modelo": Seleciona contrato publicado (status=production) filtrado por workspace */}
                {currentKey === 'template' && (
                  <WizardStepTemplate
                    contracts={productionContracts}
                    loading={isLoadingProduction}
                    onSelect={handleSelectContract}
                    dealWorkspaceId={dealWorkspaceId}
                  />
                )}
                {/* Passo "Variáveis": Revisar variáveis (apenas para modelo HTML) */}
                {currentKey === 'variables' && activeTemplateForPreview && (
                  <WizardStepVariables
                    template={activeTemplateForPreview}
                    variables={variables}
                    onChangeVariables={setVariables}
                    dealItems={dealItems}
                    onChangeDealItems={setDealItems}
                  />
                )}
                {/* Passo "Preview": Preview do contrato (apenas para modelo HTML) */}
                {currentKey === 'preview' && (
                  <WizardStepPreview
                    html={finalHtml}
                    backgroundPreviewUrl={backgroundUrl}
                    showPageNumbers={activeTemplateForPreview?.show_page_numbers}
                    pageNumberPosition={activeTemplateForPreview?.page_number_position}
                    bgMarginTopMm={activeTemplateForPreview?.bg_margin_top_mm}
                    bgMarginBottomMm={activeTemplateForPreview?.bg_margin_bottom_mm}
                    paginationYOffsetMm={activeTemplateForPreview?.pagination_y_offset_mm}
                  />
                )}
                {/* v2026-08: Passo "Veículos" — seleção que alimenta as variáveis do contrato */}
                {currentKey === 'vehicles' && (
                  <WizardStepVehicles
                    vehicles={availableVehicles}
                    isLoading={isLoadingDealVehicles}
                    selectedIds={selectedVehicleIds}
                    onChange={setSelectedVehicleIds}
                  />
                )}
                {/* v2026-05: Passo "PDF do contrato" — upload do PDF específico deste contrato */}
                {currentKey === 'upload_pdf' && (
                  <WizardStepUploadPdf
                    tenantId={tenantId}
                    uploadedPath={wizardUploadedPdfPath}
                    uploadedName={wizardUploadedPdfName}
                    isUploading={isUploadingWizardPdf}
                    onUpload={handleWizardPdfUpload}
                    onClear={handleClearWizardPdf}
                  />
                )}
                {/* Passo "Signatários": Definir signatários — pré-populados com default_signers */}
                {currentKey === 'signers' && (
                  <WizardStepSigners
                    signers={signers}
                    onChangeSigners={setSigners}
                    allowedDeliveryMethods={allowedDeliveryMethods}
                  />
                )}
                {/* Passo "Documentos": Anexar documentos exigidos */}
                {currentKey === 'documents' && (
                  <WizardStepDocuments
                    requiredDocs={requiredDocs}
                    customerDocs={customerDocs}
                    onUpload={handleDocUpload}
                    onGetPublicUrl={getPublicUrl}
                    isUploading={uploadCustomerDoc.isPending}
                  />
                )}
                {/* Passo "Confirmar": Confirmar geração */}
                {currentKey === 'confirm' && selectedContract && (() => {
                  const fixedVersionNum = selectedContract.contract_template_versions?.version_number;
                  const templateVersion = (() => {
                    if (fixedVersionNum != null) return `v1.${fixedVersionNum - 1} (fixada)`;
                    const versions = selectedContract.contract_templates?.contract_template_versions;
                    if (versions && versions.length > 0) {
                      const maxN = Math.max(...versions.map((v: { version_number: number }) => v.version_number));
                      return `v1.${maxN} (atual)`;
                    }
                    return 'v1.0 (atual)';
                  })();

                  const contentHtml = (selectedTemplate as any)?.content_html || '';
                  const parsedVarsTotal = contentHtml ? parseContractVariables(contentHtml).length : 0;
                  const parsedVarsList = contentHtml ? parseContractVariables(contentHtml) : [];
                  const filledCount = parsedVarsList.filter(
                    (v) => (variables[v.key] ?? v.defaultValue ?? '').trim() !== ''
                  ).length;

                  return (
                    <WizardStepConfirm
                      templateName={selectedContract.name}
                      title={`${selectedContract.contract_templates?.name || selectedContract.name} - ${dealData?.title || 'Contrato'}`}
                      signersCount={signers.filter((s) => s.signer_name.trim()).length}
                      variablesFilled={filledCount}
                      variablesTotal={parsedVarsTotal}
                      templateVersion={templateVersion}
                      dealTitle={dealData?.title}
                      documentsAttached={requiredDocs.filter(r => customerDocs.find(d => d.document_type_id === r.document_type_id)).length}
                      documentsRequired={requiredDocs.length}
                      uploadedPdfName={isUploadedPdfTemplate ? (wizardUploadedPdfName ?? undefined) : undefined}
                    />
                  );
                })()}
              </div>
            </ScrollArea>

            {/* Footer fixo com navegação */}
            <div className="flex items-center justify-between px-6 py-4 border-t flex-shrink-0 bg-background">
              <Button
                variant="outline"
                size="sm"
                onClick={() => step === 0 ? handleClose() : goPrevStep()}
                disabled={isSubmitting}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                {step === 0 ? 'Cancelar' : 'Voltar'}
              </Button>

              {step < STEPS.length - 1 ? (
                <Button
                  size="sm"
                  onClick={goNextStep}
                  disabled={!canNext()}
                >
                  Próximo
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>

              ) : (
                <Button
                  size="sm"
                  onClick={handleConfirm}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 mr-1" />
                  )}
                  Gerar Contrato
                </Button>
              )}
            </div>
          </main>
        </div>
      </DialogContent>
    </Dialog>
  );
}
